// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { OtaCpuType, OtaDeviceType } from "@/api/hooks/use-firmware";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const DEVICE_TYPE_CLASSES: Record<OtaDeviceType, string> = {
  esp32: "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300",
  stridingcam:
    "border-purple-300 bg-purple-50 text-purple-700 dark:border-purple-900 dark:bg-purple-950 dark:text-purple-300",
  sc25: "border-teal-300 bg-teal-50 text-teal-700 dark:border-teal-900 dark:bg-teal-950 dark:text-teal-300",
};

export function FirmwareDeviceTypeBadge({ deviceType }: { deviceType: OtaDeviceType }) {
  return (
    <Badge variant="outline" className={cn("font-mono font-normal lowercase", DEVICE_TYPE_CLASSES[deviceType])}>
      {deviceType}
    </Badge>
  );
}

export function FirmwareCpuTypeBadge({ cpuType }: { cpuType: OtaCpuType | null | undefined }) {
  if (!cpuType) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant="outline" className="font-mono font-normal lowercase text-muted-foreground">
      {cpuType}
    </Badge>
  );
}

export function FirmwareFirmwareTypeBadge({ firmwareType }: { firmwareType: string | null | undefined }) {
  if (!firmwareType) return <span className="text-muted-foreground">—</span>;
  // A partial update (e.g. calib) is called out, since flashing one partition is not the same as
  // shipping the full image that "all" represents.
  const isFullImage = firmwareType === "all";
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-mono font-normal lowercase",
        isFullImage
          ? "text-muted-foreground"
          : "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300",
      )}
    >
      {firmwareType}
    </Badge>
  );
}

export function FirmwareProductionBadge({ isProduction }: { isProduction: boolean }) {
  const { t } = useTranslation("back-office");
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-normal lowercase",
        isProduction
          ? "border-green-300 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-300"
          : "text-muted-foreground",
      )}
    >
      {isProduction ? t("firmware.production.ready") : t("firmware.production.draft")}
    </Badge>
  );
}
